import { OutcomeVariableType } from '@blisspointmedia/bpm-types/dist/Forecasting';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { AnalysisPath, DataQaGroup, DataQaResults, ForecastLadderRung } from '../../dataQaContract';
import { ComboSubmitFailure, StartScalingAnalysisRequest } from '../../scalingAnalysisContract';
import {
  pooledPlatformLabel,
  pooledTacticLabel,
  tacticDisplayLabel
} from '../../forecasting-utils';
import {
  ALL_PLATFORMS,
  ALL_TACTICS,
  Analysis,
  AnalysisType,
  Card,
  ComboInfo,
  Grain,
  GrainOption,
  GrainType,
  MINIMUM_COMPARISON_BUDGETS,
  PathViability,
  PRIMARY_COMPARISON_PCT,
  Viability
} from './request-form-constants';

export const comboKey = (ch: string, pl: string, tac: string, kpi: string) =>
  `${ch}|||${pl}|||${tac}|||${kpi}`;

// Key for a pooled-viability map: (channel, pooled dimension, kpi).
export const pooledKey = (ch: string, dim: string, kpi: string): string =>
  `${ch}|||${dim}|||${kpi}`;

// Key for the channel-only viability map: (channel, kpi).
const channelKey = (ch: string, kpi: string): string => `${ch}|||${kpi}`;

/** Does this grain pool across platforms ("All platforms")? */
export const isPlatformPooled = (grain: GrainType): boolean =>
  grain === Grain.CHANNEL_TACTIC || grain === Grain.CHANNEL;

/** Does this grain pool across tactics ("All tactics")? */
export const isTacticPooled = (grain: GrainType): boolean =>
  grain === Grain.CHANNEL_PLATFORM || grain === Grain.CHANNEL;

// Blanks whichever dimension the grain pools, to "" (never "N/A").
const pooledDims = (
  card: Pick<Card, 'platform' | 'tactic' | 'grain'>
): {
  platform: string;
  tactic: string;
} => ({
  platform: isPlatformPooled(card.grain) ? '' : card.platform,
  tactic: isTacticPooled(card.grain) ? '' : card.tactic
});

/** The analysis a card runs. The selector keeps exactly one of the two booleans true. */
export const cardAnalysis = (card: Pick<Card, 'scaling'>): AnalysisType =>
  card.scaling ? Analysis.SCALING : Analysis.FORECAST;

export const formatComboLabel = (
  card: Pick<Card, 'channel' | 'platform' | 'tactic' | 'kpi' | 'grain'>
) => {
  const { platform, tactic } = pooledDims(card);
  const dims = [card.channel, pooledPlatformLabel(platform), pooledTacticLabel(tactic)];
  return `${dims.filter(Boolean).join(' / ')} — ${card.kpi}`;
};

type SubmitKeyFields = Pick<Card, 'channel' | 'platform' | 'tactic' | 'kpi' | 'grain' | 'scaling'>;

// What a card actually submits, post-pooling -- two cards with the same key
// send the identical request even if their raw platform/tactic differ. The
// analysis is part of the key: a forecast and a scaling run of one combo are
// two different jobs.
const submitComboKey = (card: SubmitKeyFields): string => {
  const { platform, tactic } = pooledDims(card);
  return `${cardAnalysis(card)}|||${card.channel}|||${platform}|||${tactic}|||${card.kpi}|||${card.grain}`;
};

// True when two or more of the given cards would submit an identical combo.
export const hasDuplicateSubmitCombo = (cards: SubmitKeyFields[]): boolean => {
  const seen = new Set<string>();
  return cards.some(card => {
    const key = submitComboKey(card);
    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
    return false;
  });
};

const comparisonAmount = (primaryNum: number, pctNum: number): number =>
  (primaryNum * pctNum) / 100;

export const comparisonDollars = (primaryNum: number, pctNum: number): number =>
  Number.isFinite(pctNum) ? Math.round(comparisonAmount(primaryNum, pctNum)) : 0;

// Rounded to 4 decimals to avoid float drift (e.g. 33.33333333333336) while
// keeping the round-tripped $ amount within a cent or two of what was typed.
export const comparisonPct = (dollars: number, primaryNum: number): number =>
  primaryNum > 0 ? Math.round((dollars / primaryNum) * 1000000) / 10000 : 0;

// Invalid if non-numeric or outside [0, 200]; blank reads as 0%.
export const isComparisonPctInvalid = (pct: string): boolean => {
  const pctNum = Number(pct);
  return !Number.isFinite(pctNum) || pctNum > 200 || pctNum < 0;
};

// Below the floor, every row must stay on the card — none are removable.
export const isComparisonRemovable = (comparisonsCount: number): boolean =>
  comparisonsCount > MINIMUM_COMPARISON_BUDGETS;

export const EMPTY_PATH: PathViability = { viable: false, volume: false, revenue: false };

// Viable = any rung passes. Used as-is for scaling; forecast instead derives
// viability from `forecastGrainOptions`, since a rung must also survive suppression.
const anyRungPathViability = (path: AnalysisPath<ForecastLadderRung>): PathViability => ({
  viable: path.recommended_grain !== 'none',
  volume: path.ladder.some(rung => rung.offered && rung.eligible_volume),
  revenue: path.ladder.some(rung => rung.offered && rung.eligible_revenue)
});

const orPath = (a: PathViability, b: PathViability): PathViability => ({
  viable: a.viable || b.viable,
  volume: a.volume || b.volume,
  revenue: a.revenue || b.revenue
});

// Genuinely no label ("N/A"/blank) in the data — distinct from "pooled" ("").
const isUnlabelled = (value: string): boolean => !value || value === 'N/A';

// Finest → coarsest; membership and order both derive from Grain's declaration.
export const GRAIN_ORDER: GrainType[] = Object.values(Grain);

const isForecastGrainType = (grain: string): grain is GrainType =>
  GRAIN_ORDER.some(g => g === grain);

// Drops rollups whose retained dimension is unlabelled, and channel-only unless
// this is the scaling ladder (forecast never offers it). Duplicates of finest
// are still listed.
const forecastGrainOptions = (
  path: AnalysisPath<ForecastLadderRung>,
  platform: string,
  tactic: string,
  analysis: AnalysisType = Analysis.FORECAST
): GrainOption[] =>
  path.ladder
    .filter(
      (rung): rung is ForecastLadderRung & { grain: GrainType } =>
        rung.offered && rung.eligible && isForecastGrainType(rung.grain)
    )
    .filter(rung => {
      if (rung.grain === Grain.CHANNEL) {
        return analysis === Analysis.SCALING;
      }
      if (rung.grain === Grain.CHANNEL_PLATFORM) {
        return !isUnlabelled(platform);
      }
      if (rung.grain === Grain.CHANNEL_TACTIC) {
        return !isUnlabelled(tactic);
      }
      return true;
    })
    .map(rung => ({
      grain: rung.grain,
      volume: rung.eligible_volume,
      revenue: rung.eligible_revenue
    }));

// Deduped, OR-ing eligibility across duplicate QA groups.
const mergeGrainOptions = (a: GrainOption[], b: GrainOption[]): GrainOption[] => {
  const merged = new Map<GrainType, GrainOption>();
  [...a, ...b].forEach(o => {
    const existing = merged.get(o.grain);
    merged.set(o.grain, {
      grain: o.grain,
      volume: Boolean(existing?.volume) || o.volume,
      revenue: Boolean(existing?.revenue) || o.revenue
    });
  });
  return [...merged.values()].sort(
    (x, y) => GRAIN_ORDER.indexOf(x.grain) - GRAIN_ORDER.indexOf(y.grain)
  );
};

// QA's recommendation if offered, else the most granular survivor, else null.
export const recommendedGrainFor = (
  recommended: string | null,
  grains: GrainOption[]
): GrainType | null =>
  recommended !== null &&
  isForecastGrainType(recommended) &&
  grains.some(o => o.grain === recommended)
    ? recommended
    : (grains[0]?.grain ?? null);

/** Per-combo ComboInfo off the forecast ladder (default) or, with
 *  Analysis.SCALING, the scaling ladder -- see ComboInfo. */
export const buildComboLookup = (
  qa: DataQaResults,
  analysis: AnalysisType = Analysis.FORECAST
): Map<string, ComboInfo> => {
  const m = new Map<string, ComboInfo>();
  qa.groups.forEach(g => {
    const k = comboKey(g.channel, g.platform, g.tactic, g.kpi);
    const existing = m.get(k);
    const path = g.path[analysis];
    const grains = mergeGrainOptions(
      existing?.forecastGrains ?? [],
      forecastGrainOptions(path, g.platform, g.tactic, analysis)
    );
    m.set(k, {
      forecast: { viable: grains.length > 0 },
      forecastGrains: grains,
      recommendedGrain: recommendedGrainFor(path.recommended_grain, grains),
      scaling: orPath(existing?.scaling ?? EMPTY_PATH, anyRungPathViability(g.path.scaling))
    });
  });
  return m;
};

// Forecast viability only; a scaling card reads ComboInfo.scaling (any rung) instead.
export const isViable = (i?: ComboInfo) => Boolean(i?.forecast.viable);

/** Can this combo scale at any grain? Gates the selector's Scaling side. */
export const isScalingViable = (i?: ComboInfo) => Boolean(i?.scaling.viable);

// Per-grain eligibility, not the OR'd aggregate — a rollup can clear an
// outcome the finest node couldn't, or vice versa.
export const forecastViabilityForGrain = (grain: GrainType, info?: ComboInfo): PathViability => {
  const option = info?.forecastGrains.find(o => o.grain === grain);
  return option ? { viable: true, volume: option.volume, revenue: option.revenue } : EMPTY_PATH;
};

// Defensive OR across every sibling row -- doesn't assume one row alone
// carries the full pooled rung. A no-op if QA always guarantees that.
const buildPooledViability = (
  qa: DataQaResults,
  grain: GrainType,
  pooledDim: (g: DataQaGroup) => string,
  analysis: AnalysisType = Analysis.FORECAST
): Map<string, PathViability> => {
  const m = new Map<string, PathViability>();
  qa.groups.forEach(g => {
    const rung = g.path[analysis].ladder.find(r => r.grain === grain);
    const dim = pooledDim(g);
    if (!rung?.offered || !rung.eligible || isUnlabelled(dim)) {
      return;
    }
    const key = pooledKey(g.channel, dim, g.kpi);
    m.set(
      key,
      orPath(m.get(key) ?? EMPTY_PATH, {
        viable: true,
        volume: rung.eligible_volume,
        revenue: rung.eligible_revenue
      })
    );
  });
  return m;
};

/** Channel_platform viability per (channel, platform, kpi). See buildPooledViability. */
export const buildChannelPlatformViability = (
  qa: DataQaResults,
  analysis: AnalysisType = Analysis.FORECAST
): Map<string, PathViability> =>
  buildPooledViability(qa, Grain.CHANNEL_PLATFORM, g => g.platform, analysis);

/** Channel_tactic viability per (channel, tactic, kpi). See buildPooledViability. */
export const buildChannelTacticViability = (
  qa: DataQaResults,
  analysis: AnalysisType = Analysis.FORECAST
): Map<string, PathViability> =>
  buildPooledViability(qa, Grain.CHANNEL_TACTIC, g => g.tactic, analysis);

/** Channel-only viability per (channel, kpi), off the scaling ladder. Same
 *  defensive OR across sibling rows as buildPooledViability; nothing is pooled
 *  away to check for a label, since channel is always real. */
export const buildChannelViability = (qa: DataQaResults): Map<string, PathViability> => {
  const m = new Map<string, PathViability>();
  qa.groups.forEach(g => {
    const rung = g.path.scaling.ladder.find(r => r.grain === Grain.CHANNEL);
    if (!rung?.offered || !rung.eligible) {
      return;
    }
    const key = channelKey(g.channel, g.kpi);
    m.set(
      key,
      orPath(m.get(key) ?? EMPTY_PATH, {
        viable: true,
        volume: rung.eligible_volume,
        revenue: rung.eligible_revenue
      })
    );
  });
  return m;
};

/**
 * Which outcomes a card may run. An outcome must clear EVERY checked analysis
 * (the request is their Cartesian product); with none checked yet, preview
 * leniently against any viable path instead.
 */
export const availableOutcomes = (
  analyses: Pick<Card, 'forecast' | 'scaling'>,
  grain: GrainType,
  info?: ComboInfo
) => {
  const forecast = forecastViabilityForGrain(grain, info);
  const checked: PathViability[] = [];
  if (analyses.forecast && forecast.viable) {
    checked.push(forecast);
  }
  if (analyses.scaling && info?.scaling.viable) {
    checked.push(info.scaling);
  }
  if (checked.length > 0) {
    return {
      volume: checked.every(p => p.volume),
      revenue: checked.every(p => p.revenue)
    };
  }
  const viable = [forecast, info?.scaling].filter((p): p is PathViability => Boolean(p?.viable));
  return {
    volume: viable.some(p => p.volume),
    revenue: viable.some(p => p.revenue)
  };
};

// Grains reachable without changing whether platform is pooled -- a real
// platform pick can never get silently clamped onto channel_tactic.
const reachableGrains = (card: Pick<Card, 'grain'>, info?: ComboInfo): GrainOption[] => {
  const platformPooled = card.grain === Grain.CHANNEL_TACTIC;
  return (info?.forecastGrains ?? []).filter(o =>
    platformPooled ? o.grain === Grain.CHANNEL_TACTIC : o.grain !== Grain.CHANNEL_TACTIC
  );
};

/** Masks a card's picks down to what QA blessed, clamping `grain` if the combo changed underneath it. */
export const maskCard = (card: Card, info?: ComboInfo): Card => {
  const reachable = reachableGrains(card, info);
  const grain = reachable.some(o => o.grain === card.grain)
    ? card.grain
    : (reachable.find(o => o.grain === info?.recommendedGrain)?.grain ??
      reachable[0]?.grain ??
      Grain.FINEST);
  const outcomes = availableOutcomes(card, grain, info);
  const forecastViable = forecastViabilityForGrain(grain, info).viable;
  return {
    ...card,
    grain,
    forecast: card.forecast && forecastViable,
    scaling: card.scaling && Boolean(info?.scaling.viable),
    volume: card.volume && outcomes.volume,
    revenue: card.revenue && outcomes.revenue
  };
};

/** A scaling card's per-outcome eligibility at its own grain; `scalingInfo`
 *  comes from the scaling Viability, so this is the same per-grain lookup. */
export const scalingOutcomesAtGrain = (grain: GrainType, scalingInfo?: ComboInfo): PathViability =>
  forecastViabilityForGrain(grain, scalingInfo);

/**
 * maskCard's twin for a scaling card, against a ComboInfo built from the
 * scaling ladder. Outcomes gate on the card's own grain (econ fits an outcome
 * only if that grain clears it), not the any-rung aggregate. Like maskCard, a
 * stale grain is clamped without flipping whether platform is pooled.
 */
export const maskScalingCard = (card: Card, scalingInfo?: ComboInfo): Card => {
  const platformPooled = isPlatformPooled(card.grain);
  const reachable = (scalingInfo?.forecastGrains ?? []).filter(
    o => isPlatformPooled(o.grain) === platformPooled
  );
  const grain = reachable.some(o => o.grain === card.grain)
    ? card.grain
    : (reachable.find(o => o.grain === scalingInfo?.recommendedGrain)?.grain ??
      reachable[0]?.grain ??
      card.grain);
  const atGrain = scalingOutcomesAtGrain(grain, scalingInfo);
  return {
    ...card,
    grain,
    forecast: false,
    scaling: card.scaling && atGrain.viable,
    volume: card.volume && atGrain.volume,
    revenue: card.revenue && atGrain.revenue
  };
};

/** Grains a card can move to without un-pooling a dimension it pools. A pooled
 *  card still carries a platform/tactic (seeded by defaultSelection) that the
 *  user never picked; un-pooling would read and surface it. Finest -> coarsest. */
export const grainsKeepingPooled = (grain: GrainType): GrainType[] =>
  GRAIN_ORDER.filter(
    g =>
      (!isPlatformPooled(grain) || isPlatformPooled(g)) &&
      (!isTacticPooled(grain) || isTacticPooled(g))
  );

/**
 * Where a card lands when it switches to the analysis `viability` describes:
 * its own visible picks at the finest grain that analysis can run them (never
 * un-pooling a dimension). Failing that, a card that pools a dimension (whose
 * hidden platform/tactic the user never picked) takes that analysis's default
 * selection for its channel + KPI; a card showing a real platform and tactic
 * gets null instead, so its picks are never swapped for ones the user didn't
 * choose. null = that side of the selector is unavailable.
 */
export const switchTarget = (
  viability: Pick<Viability, 'comboViableAtGrain' | 'defaultSelection'>,
  card: Pick<Card, 'channel' | 'platform' | 'tactic' | 'kpi' | 'grain'>
): (Partial<Pick<Card, 'platform' | 'tactic'>> & { grain: GrainType }) | null => {
  const runsAt = (grain: GrainType) =>
    viability.comboViableAtGrain(card.channel, card.platform, card.tactic, card.kpi, grain);
  const keptGrain = runsAt(card.grain) ? card.grain : grainsKeepingPooled(card.grain).find(runsAt);
  if (keptGrain) {
    return { grain: keptGrain };
  }
  if (!isPlatformPooled(card.grain) && !isTacticPooled(card.grain)) {
    return null;
  }
  const seed = viability.defaultSelection(card.channel, card.kpi);
  return viability.comboViableAtGrain(
    card.channel,
    seed.platform,
    seed.tactic,
    card.kpi,
    seed.grain
  )
    ? seed
    : null;
};

export interface QaLookups {
  platformsByChannel: Record<string, string[]>;
  tacticsByCP: Record<string, string[]>;
  tacticsByChannel: Record<string, string[]>;
}

/** Builds a card's QA-derived viability lookups, pure over the QA run + its
 *  channel/platform/tactic universe -- extracted here so it's testable. Forecast
 *  cards use the forecast ladder (default); scaling cards pass Analysis.SCALING,
 *  which reads the scaling ladder and also offers channel-only. */
export const buildViability = (
  qa: DataQaResults,
  lookups: QaLookups,
  analysis: AnalysisType = Analysis.FORECAST
): Viability => {
  const { platformsByChannel, tacticsByCP, tacticsByChannel } = lookups;
  const comboLookup = buildComboLookup(qa, analysis);
  const channelPlatformViability = buildChannelPlatformViability(qa, analysis);
  const channelTacticViability = buildChannelTacticViability(qa, analysis);
  const channelViability =
    analysis === Analysis.SCALING ? buildChannelViability(qa) : new Map<string, PathViability>();

  const infoFor = (ch: string, pl: string, tac: string, kpi: string) =>
    comboLookup.get(comboKey(ch, pl, tac, kpi));
  const tacticsFor = (ch: string, pl: string) => tacticsByCP[`${ch}|||${pl}`] ?? [];
  const tacticsForChannel = (ch: string) => tacticsByChannel[ch] ?? [];

  // Per-grain viability, OR'd across every sibling row a pooled grain spans --
  // never read off a single row's own ComboInfo.
  const forecastViabilityAt = (
    ch: string,
    pl: string,
    tac: string,
    kpi: string,
    grain: GrainType
  ): PathViability => {
    if (grain === Grain.CHANNEL) {
      return channelViability.get(channelKey(ch, kpi)) ?? EMPTY_PATH;
    }
    if (grain === Grain.CHANNEL_PLATFORM) {
      return channelPlatformViability.get(pooledKey(ch, pl, kpi)) ?? EMPTY_PATH;
    }
    if (grain === Grain.CHANNEL_TACTIC) {
      return channelTacticViability.get(pooledKey(ch, tac, kpi)) ?? EMPTY_PATH;
    }
    return forecastViabilityForGrain(Grain.FINEST, infoFor(ch, pl, tac, kpi));
  };

  const allTacticsViableForKpi = (ch: string, pl: string, kpi: string) =>
    forecastViabilityAt(ch, pl, '', kpi, Grain.CHANNEL_PLATFORM).viable;
  const tacticViableForKpi = (ch: string, tac: string, kpi: string) =>
    forecastViabilityAt(ch, '', tac, kpi, Grain.CHANNEL_TACTIC).viable;
  const channelViableForKpi = (ch: string, kpi: string) =>
    forecastViabilityAt(ch, '', '', kpi, Grain.CHANNEL).viable;
  const allPlatformsViableForKpi = (ch: string, kpi: string) =>
    tacticsForChannel(ch).some(t => tacticViableForKpi(ch, t, kpi)) || channelViableForKpi(ch, kpi);
  const comboViableAtGrain = (ch: string, pl: string, tac: string, kpi: string, grain: GrainType) =>
    forecastViabilityAt(ch, pl, tac, kpi, grain).viable;
  const firstChannelTactic = (ch: string, kpi: string) =>
    tacticsForChannel(ch).find(t => tacticViableForKpi(ch, t, kpi));

  // Selectable only via a grain a real pick can reach (finest or
  // channel_platform) -- channel_tactic pools ACROSS platforms.
  const platformViableForKpi = (ch: string, pl: string, kpi: string) =>
    tacticsFor(ch, pl).some(t => forecastViabilityAt(ch, pl, t, kpi, Grain.FINEST).viable) ||
    allTacticsViableForKpi(ch, pl, kpi);

  // Aggregation-correct per-card view for maskCard/availableOutcomes.
  const cardInfoFor = (ch: string, pl: string, tac: string, kpi: string): ComboInfo | undefined => {
    const raw = infoFor(ch, pl, tac, kpi);
    const grains = GRAIN_ORDER.map(grain => ({
      grain,
      ...forecastViabilityAt(ch, pl, tac, kpi, grain)
    }))
      .filter(o => o.viable)
      .map(({ grain, volume, revenue }) => ({ grain, volume, revenue }));
    if (grains.length === 0) {
      return raw;
    }
    return {
      forecast: { viable: true },
      forecastGrains: grains,
      recommendedGrain: recommendedGrainFor(raw?.recommendedGrain ?? null, grains),
      scaling: raw?.scaling ?? EMPTY_PATH
    };
  };

  // Best real tactic + grain for one platform: finest first, else pooled if
  // the platform supports it, else first-available.
  const bestTacticFor = (
    ch: string,
    pl: string,
    kpi: string
  ): { tactic: string; grain: GrainType } => {
    const tactics = tacticsFor(ch, pl);
    const finestTactic = tactics.find(t => comboViableAtGrain(ch, pl, t, kpi, Grain.FINEST));
    if (finestTactic) {
      return { tactic: finestTactic, grain: Grain.FINEST };
    }
    if (allTacticsViableForKpi(ch, pl, kpi)) {
      return { tactic: tactics[0] ?? '', grain: Grain.CHANNEL_PLATFORM };
    }
    return { tactic: tactics[0] ?? '', grain: Grain.FINEST };
  };

  const defaultPlatform = (ch: string, kpi: string) => {
    const platforms = platformsByChannel[ch] ?? [];
    return platforms.find(p => platformViableForKpi(ch, p, kpi)) ?? platforms[0] ?? '';
  };

  // Priority: platform+tactic → platform+All tactics → All platforms+tactic.
  // Skips Untagged as the default pick; still selectable via the dropdown.
  const defaultSelection = (ch: string, kpi: string) => {
    const platforms = platformsByChannel[ch] ?? [];
    for (const pl of platforms) {
      const tac = tacticsFor(ch, pl).find(
        t => t !== 'N/A' && comboViableAtGrain(ch, pl, t, kpi, Grain.FINEST)
      );
      if (tac) {
        return { platform: pl, tactic: tac, grain: Grain.FINEST };
      }
    }
    for (const pl of platforms) {
      if (allTacticsViableForKpi(ch, pl, kpi)) {
        return { platform: pl, tactic: tacticsFor(ch, pl)[0] ?? '', grain: Grain.CHANNEL_PLATFORM };
      }
    }
    const channelTactic = firstChannelTactic(ch, kpi);
    if (channelTactic) {
      const pl =
        platforms.find(p => tacticsFor(ch, p).includes(channelTactic)) ?? platforms[0] ?? '';
      return { platform: pl, tactic: channelTactic, grain: Grain.CHANNEL_TACTIC };
    }
    // Scaling only (always false for forecast): pool both dimensions.
    if (channelViableForKpi(ch, kpi)) {
      const pl = platforms[0] ?? '';
      return { platform: pl, tactic: tacticsFor(ch, pl)[0] ?? '', grain: Grain.CHANNEL };
    }
    // Nothing viable -- first-available combo; maskCard clamps once checked.
    const platform = defaultPlatform(ch, kpi);
    const { tactic, grain } = bestTacticFor(ch, platform, kpi);
    return { platform, tactic, grain };
  };

  return {
    cardInfoFor,
    tacticsFor,
    tacticsForChannel,
    platformViableForKpi,
    allTacticsViableForKpi,
    tacticViableForKpi,
    allPlatformsViableForKpi,
    channelViableForKpi,
    firstChannelTactic,
    comboViableAtGrain,
    platformsByChannel,
    bestTacticFor,
    defaultSelection
  };
};

const notViableTooltip = (kpi: string) => `Not viable for ${kpi}`;

// blockedReason (e.g. a pooling rule) takes priority over plain KPI-viability.
const tooltipFor = (
  blockedReason: string | undefined,
  ok: boolean,
  kpi: string
): string | undefined => blockedReason ?? (ok ? undefined : notViableTooltip(kpi));

/** Platform dropdown options: "All platforms" pseudo-option + every real platform, greyed per KPI viability. */
export const buildPlatformOptions = (
  viability: Pick<Viability, 'allPlatformsViableForKpi' | 'platformViableForKpi'>,
  platforms: string[],
  channel: string,
  kpi: string
): MenuItem<string>[] => {
  const allPlatformsOk = viability.allPlatformsViableForKpi(channel, kpi);
  return [
    {
      value: ALL_PLATFORMS,
      label: 'All platforms',
      disabled: !allPlatformsOk,
      simpleTooltip: allPlatformsOk ? undefined : notViableTooltip(kpi)
    },
    ...platforms.map(p => {
      const platformOk = viability.platformViableForKpi(channel, p, kpi);
      return {
        value: p,
        label: p,
        disabled: !platformOk,
        simpleTooltip: platformOk ? undefined : notViableTooltip(kpi)
      };
    })
  ];
};

/** Tactic dropdown options: "All tactics" pseudo-option + the source list, greyed per KPI/pooling rules. */
export const buildTacticOptions = (
  viability: Pick<
    Viability,
    'allTacticsViableForKpi' | 'tacticViableForKpi' | 'comboViableAtGrain' | 'channelViableForKpi'
  >,
  tacticSourceList: string[],
  channel: string,
  platform: string,
  kpi: string,
  platformPooled: boolean
): MenuItem<string>[] => {
  // Under "All platforms", "All tactics" is the channel-only grain: scaling
  // only, so on the forecast Viability it stays blocked exactly as before.
  const allTacticsOk = platformPooled
    ? viability.channelViableForKpi(channel, kpi)
    : viability.allTacticsViableForKpi(channel, platform, kpi);
  const allTacticsTooltip = tooltipFor(
    platformPooled && !allTacticsOk ? 'Pick a platform or a tactic' : undefined,
    allTacticsOk,
    kpi
  );
  return [
    {
      value: ALL_TACTICS,
      label: 'All tactics',
      disabled: !allTacticsOk,
      simpleTooltip: allTacticsTooltip
    },
    ...tacticSourceList.map(t => {
      const untagged = t === 'N/A' && platformPooled;
      // Finest-specific, not any-grain: a real tactic pick always means
      // finest, so a pooling-only tactic must be greyed.
      const tacticOk = platformPooled
        ? viability.tacticViableForKpi(channel, t, kpi)
        : viability.comboViableAtGrain(channel, platform, t, kpi, Grain.FINEST);
      const tooltip = tooltipFor(
        untagged ? 'Untagged is only available for a single platform' : undefined,
        tacticOk,
        kpi
      );
      return {
        value: t,
        label: tacticDisplayLabel(t),
        disabled: untagged || !tacticOk,
        simpleTooltip: tooltip
      };
    })
  ];
};

// ---- /startNewForecast request payload ----

export interface StartNewForecastComparisonBudget {
  pctOfPrimary: number;
  amount: number;
}

export interface StartNewForecastCombo {
  channel: string;
  /** "" when `grain` pools platform (channel_tactic) — never "N/A", never a list. */
  platform: string;
  /** "" when `grain` pools tactic (channel_platform) — never "N/A", never a list. */
  tactic: string;
  kpi: string;
  grain: GrainType;
  outcomes: OutcomeVariableType[];
  primaryBudget: number;
  comparisonBudgets: StartNewForecastComparisonBudget[];
}

export interface StartNewForecastPayload {
  client: string;
  dateRange: { start: string; end: string };
  combos: StartNewForecastCombo[];
}

// Keeps the first occurrence of each distinct dollar amount.
const dedupeByAmount = (
  budgets: StartNewForecastComparisonBudget[]
): StartNewForecastComparisonBudget[] =>
  budgets.filter((budget, i) => budgets.findIndex(b => b.amount === budget.amount) === i);

/** Cards should already be forecast-checked (`selectedCards` in RequestForm). */
export const buildStartNewForecastPayload = (
  cards: Card[],
  range: DateRange,
  client: string
): StartNewForecastPayload => ({
  client,
  dateRange: { start: range.start, end: range.end },
  combos: cards.map(card => {
    const primaryBudget = Number(card.primary) || 0;
    const { platform, tactic } = pooledDims(card);
    return {
      channel: card.channel,
      platform,
      tactic,
      kpi: card.kpi,
      grain: card.grain,
      outcomes: [
        ...(card.volume ? (['Volume'] as const) : []),
        ...(card.revenue ? (['Revenue'] as const) : [])
      ],
      primaryBudget,
      // The locked row is display-only; always send 100% regardless of card state.
      comparisonBudgets: dedupeByAmount([
        { pctOfPrimary: PRIMARY_COMPARISON_PCT, amount: primaryBudget },
        ...card.comparisons
          .filter(comparison => !comparison.locked)
          .map(comparison => {
            const pctOfPrimary = Number(comparison.pct) || 0;
            return { pctOfPrimary, amount: comparisonAmount(primaryBudget, pctOfPrimary) };
          })
      ])
    };
  })
});

// ---- /startScalingAnalysis request payload ----

// A weekly amount derived from a monthly total carries long decimals; cents is plenty.
const roundToCents = (n: number): number => Math.round(n * 100) / 100;

/** Scaling cards (`card.scaling`, masked) to /startScalingAnalysis. Forecast
 *  cards go to /startNewForecast via buildStartNewForecastPayload, unchanged. */
export const buildStartScalingAnalysisPayload = (
  cards: Card[],
  client: string,
  dataQaId: string
): StartScalingAnalysisRequest => ({
  client,
  dataQaId,
  combos: cards.map(card => {
    const { platform, tactic } = pooledDims(card);
    return {
      channel: card.channel,
      platform,
      tactic,
      kpi: card.kpi,
      grain: card.grain,
      outcomes: [
        ...(card.volume ? (['Volume'] as const) : []),
        ...(card.revenue ? (['Revenue'] as const) : [])
      ],
      maxWeeklySpend: roundToCents(Number(card.maxWeeklySpend) || 0)
    };
  })
});

export interface PairedSubmitResult {
  card: Card;
  /** The forecast/scaling id the lambda assigned, when this card's combo started. */
  id?: string;
  /** Why it didn't, when it didn't. */
  failure?: string;
}

/**
 * Pairs the cards sent in one submit call with what came back. Both lambdas
 * leave failed combos out of the id list, keeping order. /startScalingAnalysis
 * names each failure by its `index` in the request; /startNewForecast has no
 * index, so a failure there is matched on the channel/platform/tactic/kpi sent.
 * A card that didn't fail takes the next id in order.
 */
export const pairSubmitResults = (
  cards: Card[],
  ids: string[],
  failures: ComboSubmitFailure[]
): PairedSubmitResult[] => {
  const unclaimed = [...failures];
  let nextId = 0;
  return cards.map((card, cardIndex) => {
    const { platform, tactic } = pooledDims(card);
    const i = unclaimed.findIndex(f =>
      typeof f.index === 'number'
        ? f.index === cardIndex
        : f.channel === card.channel &&
          (f.platform ?? '') === platform &&
          (f.tactic ?? '') === tactic &&
          f.kpi === card.kpi
    );
    if (i >= 0) {
      const [failure] = unclaimed.splice(i, 1);
      return { card, failure: failure.message };
    }
    const id = ids[nextId];
    nextId += 1;
    return id ? { card, id } : { card, failure: 'No id was returned for this combo.' };
  });
};
